import {
  ColDef,
  ColGroupDef,
  ColumnGroup,
  IAggFuncParams,
  ICellRendererParams,
  ValueGetterFunc,
} from "@ag-grid-community/core";
import clsx from "clsx";
import { FundFamilyUseAllocationTableAmount } from "gen/model/fundFamilyUseAllocationTableAmount";
import { FundFamilyUseAllocationTableAmountTypeDef } from "gen/model/fundFamilyUseAllocationTableAmountTypeDef";
import { FundFamilyUseAllocationTableColumnDef } from "gen/model/fundFamilyUseAllocationTableColumnDef";
import { FundFamilyUseAllocationTableDef } from "gen/model/fundFamilyUseAllocationTableDef";
import { FundFamilyUseAllocationTableFundDef } from "gen/model/fundFamilyUseAllocationTableFundDef";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { TFunction } from "i18next";
import { first, get, isFunction, set } from "lodash-es";
import React from "react";
import { FundFamilyUseAllocationTableAddRemoveCellRenderer } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableAddRemoveCellRenderer";
import { FundFamilyUseAllocationTableAmountCellRenderer } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableAmountCellRenderer";
import { FundFamilyUseAllocationTableAutoAllocateCellRenderer } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableAutoAllocateCellRenderer";
import { FundFamilyUseAllocationTableAutoAllocateHeader } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableAutoAllocateHeader";
import { fundFamilyUseAllocationTableCalculateAmounts } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableCalculator";
import { fundFamilyUseAllocationTableDefaultColDef } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableDefaultColDef";
import {
  FundFamilyUseAllocationTableEditorSelect,
  FundFamilyUseAllocationTableEditorText,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableEditors";
import { FundFamilyUseAllocationTableHeaderGroupComponent } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableHeaderGroupComponent";
import {
  FundFamilyUseAllocationTableColumnCellRendererProps,
  FundFamilyUseAllocationTableContext,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import {
  FundFamilyUseAllocationTableAmountColHeaderComponentParams,
  FundFamilyUseAllocationTableFundFamilyOrFundDef,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableTypes";
import {
  getPrefixForAllocationFundFamilyOrFundDef,
  isFundFamilyUseAllocationTableTotalRowNode,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import { getAgClientSideRowModel } from "shared/components/table/ag/AgGridUtils";
import { MaybernTableCellRendererQueryWrapper } from "shared/components/table/maybern/MaybernTableCellRendererQueryWrapper";
import { maybernTableSuppressKeyboardEventForInput } from "shared/components/table/maybern/MaybernTableSuppressKeyboardEventForInput";
import { isMaybernTableColumnLastInGroup } from "shared/components/table/maybern/MaybernTableUtils";
import {
  formatMoneyWithoutCurrencySymbol,
  sumMoneyStrings,
} from "shared/utils/MoneyUtils";
import { match } from "ts-pattern";

export class FundFamilyUseAllocationTableColDefFactory {
  constructor(
    private t: TFunction<["common"]>,
    private allocationDef: FundFamilyUseAllocationTableDef,
    private valueGetter:
      | ValueGetterFunc<FundFamilyUseAllocationTableRow>
      | undefined,
  ) {}

  addRemoveCol = (): ColDef<FundFamilyUseAllocationTableRow> => ({
    colId: "addRemove",
    pinned: this.#hasPinnedHeaders() ? "left" : undefined,
    width: 94,
    resizable: false,
    sortable: false,
    cellRendererSelector: (params) =>
      !isFundFamilyUseAllocationTableTotalRowNode(params.node)
        ? {
            component: FundFamilyUseAllocationTableAddRemoveCellRenderer,
          }
        : fundFamilyUseAllocationTableDefaultColDef.cellRendererSelector?.(
            params,
          ),
  });

  autoAllocateCol = (): ColDef<FundFamilyUseAllocationTableRow> => ({
    field: "is_auto_allocate",
    headerName: this.t("common:drawdown.autoAllocate"),
    headerComponent: FundFamilyUseAllocationTableAutoAllocateHeader,
    width: 135,
    resizable: false,
    cellRendererSelector: (params) =>
      isFundFamilyUseAllocationTableTotalRowNode(params.node)
        ? {}
        : {
            component: FundFamilyUseAllocationTableAutoAllocateCellRenderer,
          },
    onCellValueChanged: (params) => {
      fundFamilyUseAllocationTableCalculateAmounts(params);
    },
  });

  fundFamily = (): ColDef | ColGroupDef => {
    const { fund_family, amount_types } = this.allocationDef;
    const amountColumns = amount_types.map((amountTypeDef) =>
      this.#amountCol(fund_family, amountTypeDef),
    );
    return amountColumns.length === 1
      ? {
          ...amountColumns[0],
          headerName: fund_family.title,
        }
      : {
          groupId: "fundFamily",
          headerName: fund_family.title,
          headerClass: clsx({
            "mb-header-has-title": true,
          }),
          children: amountColumns,
        };
  };

  fundCol = (
    fundDef: FundFamilyUseAllocationTableFundDef,
  ): ColDef | ColGroupDef => {
    const { amount_types } = this.allocationDef;
    const amountColumns = amount_types.map((amountTypeDef) =>
      this.#amountCol(fundDef, amountTypeDef),
    );
    return amountColumns.length === 1
      ? {
          ...amountColumns[0],
          headerName: fundDef.title,
        }
      : {
          groupId: fundDef.fund_id,
          headerName: fundDef.title,
          headerClass: clsx({
            "mb-header-has-title": true,
          }),
          children: amountColumns,
        };
  };

  headerCol = (
    columnDef: FundFamilyUseAllocationTableColumnDef,
  ): ColDef | ColGroupDef => this.#headerOrTrailerCol(columnDef, "left");

  trailerCol = (
    columnDef: FundFamilyUseAllocationTableColumnDef,
  ): ColDef | ColGroupDef => this.#headerOrTrailerCol(columnDef, "right");

  spacerCol = () => ({
    colId: "spacer",
    sortable: false,
    resizable: false,
    width: 32,
    flex: 1,
  });

  #amountCol = (
    fundFamilyOrFundDef: FundFamilyUseAllocationTableFundFamilyOrFundDef,
    amountTypeDef: FundFamilyUseAllocationTableAmountTypeDef,
  ): ColDef<
    FundFamilyUseAllocationTableRow,
    FundFamilyUseAllocationTableAmount
  > => ({
    field: `${getPrefixForAllocationFundFamilyOrFundDef(fundFamilyOrFundDef)}.${
      amountTypeDef.id
    }` as never,
    headerName: amountTypeDef.title ?? fundFamilyOrFundDef.title,
    initialWidth: 200,
    aggFunc: this.#amountColAggFunc,
    headerClass: clsx({
      "mb-header-value-type-money": true,
    }),
    headerComponentParams: {
      fundFamilyOrFundDef,
      amountTypeDef,
    } as FundFamilyUseAllocationTableAmountColHeaderComponentParams,
    cellRendererSelector: (params) => {
      if (isFundFamilyUseAllocationTableTotalRowNode(params.node)) {
        // rendered using ag grid default renderer (see valueFormatter below)
        return undefined;
      }
      const {
        amountCellRendererSelector,
      }: FundFamilyUseAllocationTableContext = params.context;
      const overridenComponent = amountCellRendererSelector?.({
        context: params.context,
        fundFamilyOrFundDef,
        amountTypeDef,
        row: params.node.data!,
      });
      // we specifically check for undefined as null is a valid override (empty cell)
      const component =
        overridenComponent !== undefined
          ? overridenComponent
          : FundFamilyUseAllocationTableAmountCellRenderer;
      return {
        component,
        params: { fundFamilyOrFundDef, amountTypeDef },
      };
    },
    valueFormatter: (params) => {
      if (isFundFamilyUseAllocationTableTotalRowNode(params.node)) {
        const data = getAgClientSideRowModel(params.api).getRootNode()
          .aggData as
          | {
              [columnId: string]: FundFamilyUseAllocationTableAmount;
            }
          | undefined;
        const amount = data?.[params.column.getColId()]?.amount;
        return amount ? formatMoneyWithoutCurrencySymbol(amount) : "";
      }
      // all other cells are rendered with a custom renderer, so this value is not used
      return "";
    },
    valueGetter: (params) => {
      const value = this.valueGetter?.(params);
      // explicitly check for undefined to allow for null/empty values
      if (value !== undefined) {
        return value;
      }
      return get(params.data, params.colDef.field!)!;
    },
    valueSetter: (params) => {
      set(params.data, params.colDef.field!, params.newValue);
      return true;
    },
    onCellValueChanged: (params) => {
      fundFamilyUseAllocationTableCalculateAmounts(params);
    },
    comparator: (a, b) => Number(a?.amount ?? 0) - Number(b?.amount ?? 0),
  });

  #headerOrTrailerCol = (
    columnDef: FundFamilyUseAllocationTableColumnDef,
    position: "left" | "right",
  ):
    | ColDef<FundFamilyUseAllocationTableRow>
    | ColGroupDef<FundFamilyUseAllocationTableRow> => {
    if (columnDef.children) {
      return {
        groupId: columnDef.id,
        headerName: columnDef.title,
        headerGroupComponent: FundFamilyUseAllocationTableHeaderGroupComponent,
        headerClass: (params) =>
          clsx({
            "mb-header-has-title": Boolean(params.colDef.headerName),
          }),
        children: columnDef.children.map((child) =>
          this.#headerOrTrailerCol(child, position),
        ),
      };
    }
    return {
      colId: columnDef.id,
      field: `column_data.${columnDef.id}` as "column_data.type",
      headerName: columnDef.title,
      pinned: columnDef.is_pinned ? position : undefined,
      cellRendererParams: columnDef,
      initialWidth: columnDef.width || undefined,
      minWidth: columnDef.width || undefined,
      flex: columnDef.flex || undefined,
      aggFunc: this.#headerOrTrailerColAggFunc,
      columnGroupShow: columnDef.column_group_show ?? undefined,
      headerClass: (params) =>
        clsx({
          "mb-header-value-type-money": columnDef.type === "money",
          "mb-header-in-group": (
            params.column?.getParent() as ColumnGroup | null
          )?.getColGroupDef(),
          "mb-header-last-in-group": isMaybernTableColumnLastInGroup(params),
        }),
      cellClass: (params) =>
        clsx(
          isFunction(fundFamilyUseAllocationTableDefaultColDef.cellClass)
            ? fundFamilyUseAllocationTableDefaultColDef.cellClass(params)
            : fundFamilyUseAllocationTableDefaultColDef.cellClass,
          {
            "mb-cell-value-type-money": columnDef.type === "money",
            "mb-cell-in-group": (
              params.column.getParent() as ColumnGroup | null
            )?.getColGroupDef(),
            "mb-cell-last-in-group": isMaybernTableColumnLastInGroup(params),
          },
        ),
      suppressKeyboardEvent: (params) =>
        params.event.key === " " ||
        maybernTableSuppressKeyboardEventForInput(params),
      checkboxSelection:
        this.allocationDef.enable_row_selection &&
        columnDef === first(this.allocationDef.headers)
          ? (params) => !params.node.data?.is_read_only
          : false,
      showDisabledCheckboxes: true,
      cellRendererSelector: (params) => {
        if (
          isFundFamilyUseAllocationTableTotalRowNode(params.node) &&
          columnDef.type !== "money"
        ) {
          return undefined;
        }
        const comp = this.#getRendererComponent(columnDef, params);
        if (comp) {
          return {
            component: MaybernTableCellRendererQueryWrapper,
            params: {
              InnerComponent: comp,
              innerComponentProps: { columnDef },
            },
          };
        }
        return fundFamilyUseAllocationTableDefaultColDef.cellRendererSelector?.(
          params,
        );
      },
      valueGetter: (params) => {
        const value = this.valueGetter?.(params);
        // explicitly check for undefined to allow for null/empty values
        if (value !== undefined) {
          return value;
        }
        return get(params.data, params.colDef.field!)!;
      },
      valueFormatter: (params) => {
        if (columnDef.type === "money") {
          if (isFundFamilyUseAllocationTableTotalRowNode(params.node)) {
            const aggData = getAgClientSideRowModel(params.api).getRootNode()
              .aggData as
              | {
                  [columnId: string]: string | undefined;
                }
              | undefined;
            const amount = aggData?.[params.column.getColId()];
            return amount ? formatMoneyWithoutCurrencySymbol(amount) : "";
          }
          return formatMoneyWithoutCurrencySymbol(params.value);
        }
        return params.value;
      },
      onCellValueChanged: (params) => {
        fundFamilyUseAllocationTableCalculateAmounts(params);
      },
    };
  };

  #hasPinnedHeaders = () =>
    this.allocationDef.headers?.some((h) => h.is_pinned);

  #getRendererComponent = (
    columnDef: FundFamilyUseAllocationTableColumnDef,
    params: ICellRendererParams,
  ):
    | React.ComponentType<FundFamilyUseAllocationTableColumnCellRendererProps>
    | undefined => {
    const { columnCellRendererSelector }: FundFamilyUseAllocationTableContext =
      params.context;
    const rendererComponent = columnCellRendererSelector?.(columnDef, params);
    if (rendererComponent) {
      return rendererComponent;
    }
    return match(columnDef.editor?.type)
      .with("select", () => FundFamilyUseAllocationTableEditorSelect)
      .with("text", () => FundFamilyUseAllocationTableEditorText)
      .otherwise(() => undefined);
  };

  #amountColAggFunc = (
    params: IAggFuncParams<
      FundFamilyUseAllocationTableRow,
      FundFamilyUseAllocationTableAmount
    >,
  ): FundFamilyUseAllocationTableAmount => ({
    amount: sumMoneyStrings(params.values.map((it) => it?.amount)),
  });

  #headerOrTrailerColAggFunc = (params: IAggFuncParams): string =>
    sumMoneyStrings(params.values);
}
